import { useCallback } from 'react'
import { getIdentityVerifyErrorCode } from '@afterpaytouch/portal-api/identity'
import { isTerminalErrorCode } from '../../config/router'
import { useUkRegFlags } from '../../hooks/useUkRegFlags/useUkRegFlags'
import { useConsumerIdentityIsValid, useGetCreditLimitQuery, useWelcomeWithInitialLimitStatus } from '../../state'
import { WelcomeWithInitialLimitStatus } from '../../state/registration/reducer'
import { isAffordabilityRequiredError } from './NewUser/NewUser.hooks'

interface WelcomeWithInitialLimitGate {
  hasSeenWelcomeWithInitialLimit: boolean
  shouldCheckWelcomeWithInitialLimit: boolean
}

interface WelcomePanelExperience {
  creditLimitErrorCode: ReturnType<typeof getIdentityVerifyErrorCode>
  hasAffordabilityError: boolean
  isTerminalAffordabilityError: boolean
  shouldShowWelcomeWithInitialLimit: boolean
  shouldSuppressHomeModals: boolean
}

const useWelcomeWithInitialLimitGate = (isGbRegFlow: boolean): WelcomeWithInitialLimitGate => {
  const welcomeWithInitialLimitStatus = useWelcomeWithInitialLimitStatus()
  const hasSeenWelcomeWithInitialLimit = welcomeWithInitialLimitStatus === WelcomeWithInitialLimitStatus.Seen
  const isConsumerIdentityValid = useConsumerIdentityIsValid()

  return {
    hasSeenWelcomeWithInitialLimit,
    shouldCheckWelcomeWithInitialLimit: isGbRegFlow && isConsumerIdentityValid && !hasSeenWelcomeWithInitialLimit,
  }
}

export const useWelcomePanelExperience = (existingUser: boolean): WelcomePanelExperience => {
  const { isGbRegFlow, isUkRegsAffordabilityFormEnabled } = useUkRegFlags()
  const { hasSeenWelcomeWithInitialLimit, shouldCheckWelcomeWithInitialLimit } = useWelcomeWithInitialLimitGate(isGbRegFlow)
  const shouldCheckAffordability = existingUser && isUkRegsAffordabilityFormEnabled
  const {
    data: creditLimitData,
    error: creditLimitError,
    isLoading: isCreditLimitLoading,
    isFetching: isCreditLimitFetching,
  } = useGetCreditLimitQuery(undefined, { skip: !shouldCheckAffordability && !shouldCheckWelcomeWithInitialLimit })

  const shouldShowWelcomeWithInitialLimit = shouldCheckWelcomeWithInitialLimit && creditLimitData?.metadata?.isInitialLimit === true
  const isWelcomeWithInitialLimitLoading = shouldCheckWelcomeWithInitialLimit && (isCreditLimitLoading || isCreditLimitFetching)
  const shouldSuppressHomeModals = isGbRegFlow && (shouldShowWelcomeWithInitialLimit || hasSeenWelcomeWithInitialLimit || isWelcomeWithInitialLimitLoading)

  const creditLimitErrorCode = getIdentityVerifyErrorCode(creditLimitError)
  const hasAffordabilityError = shouldCheckAffordability && isAffordabilityRequiredError(creditLimitErrorCode)
  const isTerminalAffordabilityError = shouldCheckAffordability && creditLimitErrorCode != null && isTerminalErrorCode(creditLimitErrorCode)

  return {
    creditLimitErrorCode,
    hasAffordabilityError,
    isTerminalAffordabilityError,
    shouldShowWelcomeWithInitialLimit,
    shouldSuppressHomeModals,
  }
}

export const useShouldShowHomeModal = (shouldSuppressHomeModals: boolean): ((isHomeModalOpen: boolean) => boolean) =>
  useCallback((isHomeModalOpen: boolean): boolean => isHomeModalOpen && !shouldSuppressHomeModals, [shouldSuppressHomeModals])
